import { For, Show, createEffect, createSignal, onCleanup } from 'solid-js'
import { Portal } from 'solid-js/web'
import { EmailRecipient } from 'st-app/api'
import styles from '../st-support-assist-agent.module.css'

class RecipientInputProps {
    value!: [() => EmailRecipient[], (v: EmailRecipient[]) => void];
    allRecipients!: () => EmailRecipient[];
    placeholder?: string;
}

export default function RecipientInput(props: RecipientInputProps) {
    const [inputText, setInputText] = createSignal('');
    const [suggestions, setSuggestions] = createSignal<EmailRecipient[]>([]);
    const [open, setOpen] = createSignal(false);
    const [focusedIndex, setFocusedIndex] = createSignal(-1);
    const [suggestionRect, setSuggestionRect] = createSignal<DOMRect | undefined>();
    let containerRef: HTMLDivElement | undefined;
    let inputRef: HTMLInputElement | undefined;
    let suggestionListRef: HTMLDivElement | undefined;

    createEffect(() => {
        const idx = focusedIndex();
        if (idx < 0 || !suggestionListRef) return;
        (suggestionListRef.children[idx] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
    });

    const closeOnOutsideClick = (e: MouseEvent) => {
        if (containerRef && !containerRef.contains(e.target as Node)) {
            setOpen(false);
            setFocusedIndex(-1);
        }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    onCleanup(() => document.removeEventListener('mousedown', closeOnOutsideClick));

    const doSearch = (text: string) => {
        const query = text.trim().toLowerCase();
        if (!query) {
            setSuggestions([]);
            setOpen(false);
            setFocusedIndex(-1);
            return;
        }
        const selectedKeys = new Set(
            props.value[0]()
                .map(getRecipientKey)
                .filter((k): k is string => !!k)
        );
        const list = uniqueRecipients(
            props.allRecipients().filter(r =>
                r.name?.toLowerCase().includes(query) || r.email?.toLowerCase().includes(query)
            )
        ).filter(r => {
            const key = getRecipientKey(r);
            return !key || !selectedKeys.has(key);
        });
        setSuggestions(list);
        setFocusedIndex(-1);
        if (list.length > 0) {
            setSuggestionRect(containerRef?.getBoundingClientRect());
            setOpen(true);
        } else {
            setOpen(false);
        }
    };

    const addRecipient = (r: EmailRecipient) => {
        if (isDuplicateRecipient(r, props.value[0]())) {
            setInputText('');
            setSuggestions([]);
            setOpen(false);
            setFocusedIndex(-1);
            inputRef?.focus();
            return;
        }

        props.value[1]([...props.value[0](), r]);
        setInputText('');
        setSuggestions([]);
        setOpen(false);
        setFocusedIndex(-1);
        inputRef?.focus();
    };

    const removeRecipient = (idx: number) => {
        props.value[1](props.value[0]().filter((_, i) => i !== idx));
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        const list = suggestions();

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open() || list.length === 0) return;
            setFocusedIndex(i => Math.min(i + 1, list.length - 1));
            return;
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open() || list.length === 0) return;
            setFocusedIndex(i => Math.max(i - 1, -1));
            return;
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (open() && list.length > 0) {
                // Select focused item, or default to first
                const idx = focusedIndex() >= 0 ? focusedIndex() : 0;
                addRecipient(list[idx]);
            } else {
                const text = inputText().trim();
                if (isValidEmail(text)) addRecipient({ email: text, isEdupointStaff: false });
            }
            return;
        }

        if (e.key === ',') {
            const text = inputText().trim();
            if (isValidEmail(text)) {
                e.preventDefault();
                addRecipient({ email: text, isEdupointStaff: false });
            }
            return;
        }

        if (e.key === 'Backspace' && !inputText()) {
            const current = props.value[0]();
            if (current.length > 0) props.value[1](current.slice(0, -1));
            return;
        }

        if (e.key === 'Escape') {
            setOpen(false);
            setFocusedIndex(-1);
        }
    };

    const handleInput = (e: InputEvent) => {
        const text = (e.currentTarget as HTMLInputElement).value;
        setInputText(text);
        doSearch(text);
    };

    const handleBlur = () => {
        const text = inputText().trim();
        if (isValidEmail(text)) addRecipient({ email: text, isEdupointStaff: false });
        setOpen(false);
        setFocusedIndex(-1);
    };

    return (
        <>
            <div
                class={styles['recipient-input']}
                ref={el => { containerRef = el; }}
                onClick={() => inputRef?.focus()}
            >
                <For each={props.value[0]()}>
                    {(r, i) => (
                        <span
                            class={`${styles['recipient-chip']}${r.isEdupointStaff != null ? ` ${r.isEdupointStaff ? styles['recipient-chip--edupoint'] : styles['recipient-chip--district']}` : ''}`}
                            title={r.email}
                        >
                            <span class={styles['recipient-chip-label']}>{getRecipientLabel(r, props.allRecipients())}</span>
                            <button
                                class={styles['recipient-chip-remove']}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); removeRecipient(i()); }}
                            >×</button>
                        </span>
                    )}
                </For>
                <input
                    ref={el => { inputRef = el; }}
                    class={styles['recipient-input-field']}
                    type="search"
                    value={inputText()}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={props.value[0]().length === 0 ? (props.placeholder ?? '') : ''}
                />
            </div>
            <Show when={open() && suggestions().length > 0}>
                <Portal>
                    <div
                        class={styles['recipient-suggestions']}
                        ref={el => { suggestionListRef = el; }}
                        style={{
                            position: 'fixed',
                            top: `${suggestionRect()?.bottom ?? 0}px`,
                            left: `${suggestionRect()?.left ?? 0}px`,
                            'min-width': `${suggestionRect()?.width ?? 240}px`,
                            'z-index': '10000',
                        }}
                    >
                        <For each={suggestions()}>
                            {(s, i) => (
                                <button
                                    class={`${styles['recipient-suggestion-item']}${i() === focusedIndex() ? ` ${styles['recipient-suggestion-item--focused']}` : ''}`}
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); addRecipient(s); }}
                                    onMouseEnter={() => setFocusedIndex(i())}
                                >
                                    <span>{s.name || s.email}</span>
                                    {s.isEdupointStaff != null && (
                                        <span class={s.isEdupointStaff
                                            ? styles['recipient-badge--edupoint']
                                            : styles['recipient-badge--district']}>
                                            {s.isEdupointStaff ? 'Edupoint' : 'District'}
                                        </span>
                                    )}
                                    <span class={styles['recipient-suggestion-email']}>{s.email}</span>
                                </button>
                            )}
                        </For>
                    </div>
                </Portal>
            </Show>
        </>
    );
}

type EmailRecipientWithUserGU = EmailRecipient & { userGU?: string };

function getRecipientKey(recipient: EmailRecipient): string | undefined {
    const recipientWithUser = recipient as EmailRecipientWithUserGU;
    if (recipientWithUser.userGU?.trim()) return `user:${recipientWithUser.userGU.trim()}`;
    if (recipient.email?.trim()) return `email:${recipient.email.trim().toLowerCase()}`;
    if (recipient.name?.trim()) return `name:${recipient.name.trim().toLowerCase()}`;
    return undefined;
}

function uniqueRecipients(recipients: EmailRecipient[]): EmailRecipient[] {
    const keys = new Set<string>();
    const unique: EmailRecipient[] = [];

    for (const recipient of recipients) {
        const key = getRecipientKey(recipient);
        if (!key || !keys.has(key)) {
            if (key) keys.add(key);
            unique.push(recipient);
        }
    }

    return unique;
}

function isDuplicateRecipient(candidate: EmailRecipient, current: EmailRecipient[]): boolean {
    const candidateKey = getRecipientKey(candidate);
    if (!candidateKey) return false;
    return current.some(r => getRecipientKey(r) === candidateKey);
}

function getRecipientLabel(recipient: EmailRecipient, allRecipients: EmailRecipient[]): string {
    if (recipient.name) return recipient.name;
    const recipientKey = getRecipientKey(recipient);
    if (recipientKey) {
        const match = allRecipients.find(r => getRecipientKey(r) === recipientKey && !!r.name);
        if (match?.name) return match.name;
    }
    // Fallback: match by email for recipients seeded without a userGU (e.g. clientEmail)
    if (recipient.email?.trim()) {
        const emailLower = recipient.email.trim().toLowerCase();
        const match = allRecipients.find(r => r.email?.trim().toLowerCase() === emailLower && !!r.name);
        if (match?.name) return match.name;
    }
    return recipient.email || '';
}

function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
